import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, unref as _unref, createBlock as _createBlock, createTextVNode as _createTextVNode } from "vue";
const _hoisted_1 = { class: "flex flex-col gap-4 mt-4 p-4 bg-white rounded-xl border border-gray-200" };
const _hoisted_2 = { class: "text-sm font-semibold text-gray-900" };
const _hoisted_3 = { class: "flex flex-col gap-2" };
const _hoisted_4 = { class: "text-xs font-medium text-gray-500 uppercase tracking-wide" };
const _hoisted_5 = {
    key: 0,
    class: "flex flex-col gap-1.5"
};
const _hoisted_6 = {
    key: 1,
    class: "text-xs text-gray-400 italic"
};
const _hoisted_7 = {
    key: 2,
    class: "flex flex-col gap-1.5"
};
const _hoisted_8 = ["checked", "disabled", "onChange"];
const _hoisted_9 = { class: "flex-1 text-sm text-gray-800" };
const _hoisted_10 = {
    key: 0,
    class: "w-4 h-4 text-gray-400 animate-spin shrink-0",
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 24 24"
};
const _hoisted_11 = { class: "flex flex-col gap-2" };
const _hoisted_12 = { class: "text-xs font-medium text-gray-500 uppercase tracking-wide" };
const _hoisted_13 = {
    key: 0,
    class: "flex flex-col gap-1.5"
};
const _hoisted_14 = {
    key: 1,
    class: "flex flex-col gap-1.5"
};
import { ref, computed, watch, onMounted } from 'vue';
import { useToast, POSITION } from 'vue-toastification';
import { translateApp } from '@/i18n';
import { useServiceCharges } from '../composables/useServiceCharges';
import { useChargeTemplateStore } from '../stores/charge-template.store';
import CustomChargeRow from './CustomChargeRow.vue';
export default /*@__PURE__*/ _defineComponent({
    __name: 'ServiceChargesSection',
    props: {
        serviceId: {},
        readOnly: { type: Boolean }
    },
    setup(__props) {
        const props = __props;
        const toast = useToast();
        const templateStore = useChargeTemplateStore();
        const { attachedTemplateIds, customCharges, isTemplateAttached, isTemplateLoading, isSaving, isLoading, error, initForService, attachTemplate, detachTemplate, addCustomCharge, editCustomCharge, removeCustomCharge, $reset, } = useServiceCharges();
        const templates = computed(() => templateStore.templates);
        const isLoadingTemplates = computed(() => templateStore.isLoading);
        // Local state for unsaved new rows
        const newRows = ref([]);
        watch(() => props.serviceId, async (id) => {
            newRows.value = [];
            if (id) {
                await initForService(id);
                if (error.value) {
                    toast.error(translateApp('services.charges.toast.load_error'), { timeout: 3000, position: POSITION.TOP_RIGHT });
                }
            }
            else {
                $reset();
            }
        }, { immediate: true });
        onMounted(() => {
            if (templateStore.templates.length === 0) {
                templateStore.fetchTemplates();
            }
        });
        async function handleTemplateToggle(templateId, checked) {
            const template = templates.value.find((t) => t.id === templateId);
            if (template?.is_mandatory && !checked)
                return;
            try {
                if (checked) {
                    await attachTemplate(templateId);
                }
                else {
                    await detachTemplate(templateId);
                }
            }
            catch (err) {
                const status = getResponseStatus(err);
                if (status === 409) {
                    toast.error(translateApp('services.charges.toast.already_attached'), { timeout: 3000, position: POSITION.TOP_RIGHT });
                }
                else {
                    toast.error(translateApp('services.charges.toast.update_error'), { timeout: 3000, position: POSITION.TOP_RIGHT });
                }
            }
        }
        function isTemplateSelected(template) {
            return Boolean(template.is_mandatory) || isTemplateAttached.value(template.id);
        }
        function isTemplateDisabled(template) {
            return (Boolean(template.is_mandatory) ||
                isTemplateLoading.value(template.id) ||
                isLoading.value ||
                Boolean(props.readOnly));
        }
        function isTemplateBusy(templateId) {
            return isTemplateLoading.value(templateId);
        }
        function addNewRow() {
            newRows.value = [...newRows.value, Date.now()];
        }
        function removeNewRow(index) {
            newRows.value = newRows.value.filter((_, i) => i !== index);
        }
        async function handleCreateCustom(rowIndex, dto) {
            try {
                await addCustomCharge(dto);
                removeNewRow(rowIndex);
                toast.success(translateApp('services.charges.toast.custom_created'), { timeout: 3000, position: POSITION.TOP_RIGHT });
            }
            catch {
                toast.error(translateApp('services.charges.toast.custom_create_error'), { timeout: 3000, position: POSITION.TOP_RIGHT });
            }
        }
        async function handleEditCustom(chargeId, dto) {
            try {
                await editCustomCharge(chargeId, dto);
            }
            catch {
                toast.error(translateApp('services.charges.toast.custom_update_error'), { timeout: 3000, position: POSITION.TOP_RIGHT });
            }
        }
        async function handleDeleteCustom(chargeId) {
            try {
                await removeCustomCharge(chargeId);
            }
            catch {
                toast.error(translateApp('services.charges.toast.custom_delete_error'), { timeout: 3000, position: POSITION.TOP_RIGHT });
            }
        }
        function getResponseStatus(err) {
            if (err && typeof err === 'object' && 'response' in err) {
                return err.response?.status ?? null;
            }
            return null;
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("h3", _hoisted_2, _toDisplayString(_ctx.$t("services.charges.service.title")), 1),
                _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.$t("services.charges.service.global_charges")), 1),
                    (isLoadingTemplates.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(2, (i) => {
                                return _createElementVNode("div", {
                                    key: i,
                                    class: "h-9 bg-gray-100 rounded-lg animate-pulse"
                                });
                            }), 64))
                        ]))
                        : (!templates.value.length)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(_ctx.$t("services.charges.service.no_global_charges")), 1))
                            : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(templates.value, (template) => {
                                    return (_openBlock(), _createElementBlock("label", {
                                        key: template.id,
                                        class: _normalizeClass(["flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors", { 'opacity-60 pointer-events-none': isTemplateDisabled(template) }])
                                    }, [
                                        _createElementVNode("input", {
                                            type: "checkbox",
                                            class: "w-4 h-4 text-egreen border-gray-300 rounded focus:ring-egreen",
                                            checked: isTemplateSelected(template),
                                            disabled: isTemplateDisabled(template),
                                            onChange: ($event) => (handleTemplateToggle(template.id, $event.target.checked))
                                        }, null, 40, _hoisted_8),
                                        _createElementVNode("span", _hoisted_9, _toDisplayString(template.name), 1),
                                        _createElementVNode("span", {
                                            class: _normalizeClass(["inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium shrink-0", template.type === 'FLAT' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'])
                                        }, _toDisplayString(template.type === 'FLAT' ? `$${template.amount.toFixed(2)}` : `${template.amount}%`), 3),
                                        (isTemplateBusy(template.id))
                                            ? (_openBlock(), _createElementBlock("svg", _hoisted_10, [...(_cache[0] || (_cache[0] = [
                                                    _createElementVNode("circle", {
                                                        class: "opacity-25",
                                                        cx: "12",
                                                        cy: "12",
                                                        r: "10",
                                                        stroke: "currentColor",
                                                        "stroke-width": "4"
                                                    }, null, -1),
                                                    _createElementVNode("path", {
                                                        class: "opacity-75",
                                                        fill: "currentColor",
                                                        d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                    }, null, -1)
                                                ]))]))
                                            : _createCommentVNode("", true)
                                    ], 2));
                                }), 128))
                            ]))
                ]),
                _createElementVNode("div", _hoisted_11, [
                    _createElementVNode("p", _hoisted_12, _toDisplayString(_ctx.$t("services.charges.service.custom_charges")), 1),
                    (_unref(isLoading))
                        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(2, (i) => {
                                return _createElementVNode("div", {
                                    key: i,
                                    class: "h-9 bg-gray-100 rounded-lg animate-pulse"
                                });
                            }), 64))
                        ]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_14, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(customCharges), (charge) => {
                                return (_openBlock(), _createBlock(CustomChargeRow, {
                                    key: charge.id,
                                    charge: charge,
                                    "is-saving": _unref(isSaving),
                                    onSave: (dto) => handleEditCustom(charge.id, dto),
                                    onDeleted: ($event) => (handleDeleteCustom(charge.id))
                                }, null, 8, ["charge", "is-saving", "onSave", "onDeleted"]));
                            }), 128)),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(newRows.value, (_, i) => {
                                return (_openBlock(), _createBlock(CustomChargeRow, {
                                    key: `new-${i}`,
                                    "is-saving": _unref(isSaving),
                                    onSave: (dto) => handleCreateCustom(i, dto),
                                    onDeleted: ($event) => (removeNewRow(i))
                                }, null, 8, ["is-saving", "onSave", "onDeleted"]));
                            }), 128))
                        ])),
                    (!__props.readOnly)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 2,
                            type: "button",
                            class: "flex items-center gap-1.5 text-sm text-egreen font-medium hover:underline transition-colors self-start",
                            onClick: addNewRow
                        }, [
                            _cache[1] || (_cache[1] = _createElementVNode("svg", {
                                class: "w-4 h-4",
                                xmlns: "http://www.w3.org/2000/svg",
                                fill: "none",
                                viewBox: "0 0 24 24",
                                "stroke-width": "2",
                                stroke: "currentColor"
                            }, [
                                _createElementVNode("path", {
                                    "stroke-linecap": "round",
                                    "stroke-linejoin": "round",
                                    d: "M12 4.5v15m7.5-7.5h-15"
                                })
                            ], -1)),
                            _createTextVNode(" " + _toDisplayString(_ctx.$t("services.charges.actions.add_custom_charge")), 1)
                        ]))
                        : _createCommentVNode("", true)
                ])
            ]));
        };
    }
});
